import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "./Header";
import Footer from "./Footer";
import DynamicTitle from "./DynamicTitle";
import { trackEvent } from "../utils/posthog";

function UserProfile() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState(null);

  const fetchUserProfile = async () => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const response = await axios.get("/api/profile", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        console.log("Profile data received:", response.data); // Debug log
        setProfileData(response.data);
      } catch (error) {
        console.error("Failed to fetch user profile:", error);
      }
    }
  };

  useEffect(() => {
    trackEvent("profile_viewed");
    fetchUserProfile();
  }, []);

  const handleManageSubscription = async () => {
    trackEvent("manage_subscription_clicked");
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        "/api/create-portal-session",
        { customer_id: user.stripe_customer_id },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        console.error("No URL found in response");
      }
    } catch (error) {
      console.error("Failed to create portal session:", error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === "N/A") return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const getSubscriptionBadgeColor = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "bg-green-100 text-green-800 border-green-300";
      case "trialing":
        return "bg-blue-100 text-blue-800 border-blue-300";
      case "past_due":
        return "bg-yellow-100 text-yellow-800 border-yellow-300";
      case "canceled":
      case "unpaid":
        return "bg-red-100 text-red-800 border-red-300";
      default:
        return "bg-gray-100 text-gray-800 border-gray-300";
    }
  };

  return (
    <div className="user-profile flex flex-col min-h-screen bg-gray-50">
      <DynamicTitle>Profile</DynamicTitle>
      <Header />
      <main className="user-profile-main flex-grow mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl mt-8 mb-8">
        {profileData ? (
          <div className="space-y-6">
            {/* Welcome Header */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Welcome back, {profileData.first_name || "User"}!
              </h1>
              <p className="text-gray-600">
                Manage your account settings and subscription here.
              </p>
            </div>

            {/* Account Information */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                Account Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Email</p>
                  <p className="text-base font-medium text-gray-900">
                    {user?.email || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Member Since</p>
                  <p className="text-base font-medium text-gray-900">
                    {user?.created_at
                      ? formatDate(user.created_at)
                      : "N/A"}
                  </p>
                </div>
              </div>
            </div>

            {/* Subscription Status */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                Subscription
              </h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Status</p>
                    <span
                      className={`inline-block px-3 py-1 rounded-full text-sm font-semibold border ${getSubscriptionBadgeColor(
                        profileData.subscription_status
                      )}`}
                    >
                      {profileData.subscription_status || "Unknown"}
                    </span>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500 mb-1">
                      {profileData.subscription_status === "active" ||
                      profileData.subscription_status === "trialing"
                        ? "Renews"
                        : "Expires"}{" "}
                      On
                    </p>
                    <p className="text-base font-medium text-gray-900">
                      {formatDate(profileData.subscription_end_date)}
                    </p>
                  </div>
                </div>
                <button
                  className="w-full md:w-auto px-6 py-3 bg-accent hover:bg-accentDark text-white font-semibold rounded-lg transition-colors"
                  onClick={handleManageSubscription}
                >
                  Manage Subscription
                </button>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                Quick Actions
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <a
                  href="/sessions"
                  className="flex items-center p-4 border border-gray-200 rounded-lg hover:border-accent hover:bg-accent hover:bg-opacity-5 transition-colors"
                >
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">
                      Start a Session
                    </h3>
                    <p className="text-sm text-gray-600">
                      Begin a new bilateral stimulation session
                    </p>
                  </div>
                  <span className="text-accent text-xl">→</span>
                </a>
                <a
                  href="/session-history"
                  className="flex items-center p-4 border border-gray-200 rounded-lg hover:border-accent hover:bg-accent hover:bg-opacity-5 transition-colors"
                >
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">
                      View History
                    </h3>
                    <p className="text-sm text-gray-600">
                      Review your past sessions
                    </p>
                  </div>
                  <span className="text-accent text-xl">→</span>
                </a>
              </div>

              {/* Tour */}
              <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900">Interactive Tour</h3>
                  <p className="text-sm text-gray-600">Walk through how sessions work</p>
                </div>
                <button
                  onClick={() => {
                    trackEvent("retake_tour_clicked");
                    navigate("/sessions/tour");
                  }}
                  className="text-sm font-medium text-accent hover:underline"
                >
                  Retake tour →
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <div className="animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-1/4 mx-auto mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
            </div>
            <p className="text-gray-600 mt-4">Loading profile...</p>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

export default UserProfile;
